import Reflux from 'reflux';
import BaseMixin from 'flux/stores/BaseMixin';
import AlertActions from 'flux/actions/AlertActions';
import Messages from 'shared/constants/Messages';
import UIRegions from 'shared/constants/UIRegions';
import SearchActions from 'flux/actions/SearchActions';
import CuesheetActions from 'flux/actions/cuesheet/CuesheetActions';
import CuesheetStore from 'flux/stores/cuesheet/CuesheetStore';
import CuesheetCoreMixin from 'flux/stores/cuesheet/CuesheetCoreMixin';
import CuesheetRowActions from 'flux/actions/cuesheet/CuesheetRowActions';
import { OrderedMap, Map, List, fromJS } from 'immutable';
import { EditableCellTypes } from 'flux/stores/cuesheet/CuesheetConstants';
import UserActions from 'flux/actions/UserActions';
import ActivityAuditListActions from 'flux/actions/ActivityAuditListActions';
import CuesheetCellActions from 'flux/actions/cuesheet/CuesheetCellActions';
import {
  replaceTextInRawRTEState,
  getPlainTextFromRawRTEState,
} from '@shoflo-rundown/draft-js-utils';

import mobileCheck from 'shared/lib/mobileCheck';
import escapeRegex from '../helpers/escapeRegex';
let onLoadFromSessionCompleted = UserActions.loadFromSession.completed;
let onCuesheetPopulate = CuesheetActions.populate;

const isMobile = mobileCheck();

export default Reflux.createStore({
  mixins: [BaseMixin, CuesheetCoreMixin],

  listenables: [SearchActions],

  init() {
    this._init();

    //Don't use search/find/replace in mobile!
    if (isMobile) return;

    CuesheetStore.listen(this.onCuesheetStoreStateChange);
    this.listenTo(onLoadFromSessionCompleted, 'onLoadUserFromSessionCompleted');
    this.listenTo(onCuesheetPopulate, 'onCuesheetPopulate');
    this.listenTo(CuesheetRowActions.toggleRowLock, 'onToggleRowLock');
  },

  _init() {
    const state = this.getState();

    this.setStateAndTrigger(
      Map({
        visible: false,
        index: List(),
        results: List(),
        replaceVal: '',
        searchQuery: '',
        noResultsTitle: 'No Results!',
        noResultsSubtitle: 'Enter your search term above',
        resetReplaceAllClick: false,
        itemIsChecked: false,
        searchFocusState: true,
        replaceFocusState: false,
        userId: state ? this.getInState('userId') : null,
      }),
    );
  },

  onLoadUserFromSessionCompleted(user) {
    //Don't use search/find/replace in mobile!
    if (isMobile) return;

    this.setInState('userId', user._id);
  },

  onCuesheetPopulate() {
    /**
     * When cuesheet populate is called clear old state
     */
    this._init();
  },

  onToggleRowLock() {
    this._onSearch(this.getState().get('searchQuery'));
  },

  onCuesheetStoreStateChange(state) {
    //Don't use search/find/replace in mobile!
    if (isMobile) return;

    const rows = state.get('rows');
    const rowMap = state.get('rowMap');
    const columnOrder = state.get('columnOrder');
    let next = this.getState()
      .set('showId', state.get('showId'))
      .set('show', state.get('show'))
      .set('rows', rows)
      .set('rowMap', rowMap);

    const lastAction = state.get('lastAction'),
      lastActionTimestamp = state.get('lastActionTimestamp');

    if (
      lastAction === 'setCursor' &&
      Math.abs(lastActionTimestamp - new Date().valueOf()) <= 500
    ) {
      return;
    }

    if (!rows || !rowMap || !columnOrder) return;

    this._prevRows = rows;
    this._prevRowMap = rowMap;
    this._prevColOrder = columnOrder;

    //Clear search index before populating it again
    next = next.set('index', List());

    this.setStateAndTrigger(next);

    this._buildSearchIndex(rows, rowMap, columnOrder);
  },

  /**
   * Set the active search result index
   */
  onSetActiveResultIndex(index) {
    //Don't use search/find/replace in mobile!
    if (isMobile) return;

    this.setInStateAndTrigger('activeResultIndex', index);

    let result = this.getInState(['results', index]);
    let itemSetId = result.get('path')[0],
      rowId = result.get('path')[2],
      showId = this.getInState('showId'),
      skipScroll = false;

    if (!showId || !itemSetId || !rowId) return;

    CuesheetRowActions.scrollToRow(
      showId,
      itemSetId,
      rowId,
      result.get('cellSpec'),
      skipScroll,
    );
  },

  /**
   * Show/Hide search panel
   */
  onToggleVisibility(display) {
    //Don't use search/find/replace in mobile!
    if (isMobile) return;

    let next = this.getState().update('visible', (v) => {
      return typeof display === 'boolean' ? display : !v;
    });

    if (this.getInState('visible'))
      ActivityAuditListActions.toggleVisibility(false);

    next = next
      .delete('results')
      .set('searchQuery', '')
      .set('noResultsTitle', 'No Results!')
      .set('noResultsSubtitle', 'Enter your search term above');

    this.setStateAndTrigger(next);
  },

  /**
   * Loop through each row and create indexes for searchable text
   */
  _buildSearchIndex(rows, rowMap, columnOrder) {
    rows.forEach((row, rowIndex) => {
      return this._createRowIndexes(
        row.get('path'),
        rowIndex,
        rowMap,
        columnOrder,
      );
    });
  },

  /**
   * Create indexes for titles, descriptions (if displayed), data cells, and current
   * users notes for row.
   */
  _createRowIndexes(path, rowIndex, rowMap, columnOrder) {
    const item = rowMap.getIn(path);

    let next = this.getState().update('index', (i) => {
      const entryTemplate = Map({
        path,
        rowIndex,
        number: item.get('numberAdjusted'),
        locked: item.get('locked'),
      });

      let index = List();

      //Add Item Title Index
      index = index.push(
        entryTemplate.merge({
          text: item.get('title'),
          rteState: item.get('rteTitleState'),
          cellSpec: Map({ type: EditableCellTypes.ItemTitle }),
        }),
      );

      //Add Item Description Index
      if (
        item.get('description') &&
        this.getInState(['show', 'displayItemDescriptions'])
      ) {
        index = index.push(
          entryTemplate.merge({
            text: item.get('description'),
            rteState: item.get('rteDescriptionState'),
            cellSpec: Map({ type: EditableCellTypes.ItemDescription }),
          }),
        );
      }

      /**
       * Add Cell Indexes
       *
       * We use columnOrder because we only want to index text from columns that
       * are visible.
       */
      columnOrder.forEach((columnId) => {
        const cell = item.getIn(['cells', columnId]);

        //Don't create index if there is no text
        if (!cell || !cell.get('text')) return;

        index = index.push(
          entryTemplate.merge({
            rteState: cell.get('rteState'),
            text: cell.get('text'),
            cellSpec: Map({ type: EditableCellTypes.Data, value: columnId }),
          }),
        );
      });

      /**
       * Add User Note Indexes
       *
       */

      const userNoteSettings = item.getIn([
        'userSettings',
        this.getInState('userId'),
        'note',
      ]);

      if (userNoteSettings) {
        const noteTemplate = {
          cellSpec: Map({
            type: EditableCellTypes.ItemNote,
            value: EditableCellTypes.ItemNote,
          }),
        };

        let text = userNoteSettings.get('text');
        const rteState = userNoteSettings.get('rteState');

        if (!text && rteState) {
          text = getPlainTextFromRawRTEState(rteState.toJS());
        }

        if (text) noteTemplate.text = text;
        if (rteState) noteTemplate.rteState = rteState;

        index = index.push(entryTemplate.merge(noteTemplate));
      }

      return i.concat(index);
    });

    this.setState(next);
  },

  /**
   * Used to disable Replace Checked button if no
   * search result items are checked
   */
  _checkForReplaceResultSelections() {
    let next = this.getState();
    let oneChecked = false;

    let results = next.get('results');

    // Used to disable Replace Checked btn if no search result items are checked
    results.forEach((result) => {
      if (result.get('replace') === true) {
        oneChecked = true;
      }
    });

    if (oneChecked) {
      next = next.set('itemIsChecked', true);
    } else {
      next = next.set('itemIsChecked', false);
    }

    this.setStateAndTrigger(next);
  },

  setReplaceInputVal(val) {
    let next = this.getState().set('replaceVal', val);

    this.setStateAndTrigger(next);
  },

  onAddReplaceToResult(index) {
    let next = this.getState();
    next = this._addReplaceToResult(index, next);

    this.setStateAndTrigger(next);
  },

  _addReplaceToResult(index, next) {
    return next
      .setIn(['results', index, 'replace'], true)
      .set('itemIsChecked', true);
  },

  onRemoveReplaceFromResult(index) {
    let next = this.getState();
    next = next.deleteIn(['results', index, 'replace']);

    this.setStateAndTrigger(next);

    this._checkForReplaceResultSelections();
  },

  onReplaceAll() {
    SearchActions.replaceChecked();
  },

  onReplaceChecked() {
    let next = this.getState();
    const results = next.get('results');
    const query = next.get('searchQuery');
    const replaceVal = next.get('replaceVal');

    let hasLockedRows =
      results &&
      results.findIndex(
        (result) => result.get('replace') && result.get('locked'),
      ) !== -1;

    if (hasLockedRows) {
      return AlertActions.show(Messages.lockedRowModal, UIRegions.Cuesheet);
    }

    /**
     * Changes Example:
     * {
     *  rowId: {columnId: {text: '', format: {}}},
     *  rowId: {columnId: {text: '', format: {}}},
     * }
     */
    let changes = OrderedMap();
    changes = changes.withMutations((data) => {
      results.forEach((result) => {
        if (!result.get('replace')) return;

        let resultState = result.get('rteState').toJS();
        let rowId = result.get('path')[2],
          columnId = result.getIn(['cellSpec', 'value']);

        if (
          result.getIn(['cellSpec', 'type']) === EditableCellTypes.ItemTitle ||
          result.getIn(['cellSpec', 'type']) ===
            EditableCellTypes.ItemDescription
        ) {
          columnId = result.getIn(['cellSpec', 'type']);
        } else {
          columnId = result.getIn(['cellSpec', 'value']);
        }

        resultState = replaceTextInRawRTEState(
          resultState,
          escapeRegex(query),
          replaceVal,
        );

        const cellSpec = result.get('cellSpec');

        const cellChanges = {
          rteState: resultState,
          format: this.getCellFormat({ rowId, cellSpec }),
          userId: this.getInState('userId'),
        };

        data.setIn([rowId, columnId], fromJS(cellChanges));
      });

      return data;
    });

    this._checkForReplaceResultSelections();

    next = next
      .set('noResultsSubtitle', 'Search for a new term above')
      .set('noResultsTitle', 'Replacement Successful!');

    next = next.delete('results');

    this.setStateAndTrigger(next);

    CuesheetCellActions.saveMultipleDataCellChanges(changes);
  },

  onSetSearchFocusState(focusState) {
    let next = this.getState();
    next = next.set('searchFocusState', focusState);

    this.setStateAndTrigger(next);
  },

  onSetReplaceFocusState(focusState) {
    let next = this.getState();
    next = next.set('replaceFocusState', focusState);

    this.setStateAndTrigger(next);
  },

  onSetReplaceAllClickState(resetReplaceAllClick) {
    let next = this.getState();
    let results = next.get('results');

    if (!resetReplaceAllClick) {
      results.forEach((result, indx) => {
        next = this._addReplaceToResult(indx, next);
      });
    }

    next = next.set('resetReplaceAllClick', resetReplaceAllClick);

    this.setStateAndTrigger(next);
  },

  refreshList() {
    let next = this.getState();
    let query = next.get('searchQuery');

    next = next.delete('results');
    SearchActions.search(query);

    this.setStateAndTrigger(next);
  },

  /**
   * Retrieve search indexes that match the search query
   * @param isActualSearch: is used to check when the search is an
   *    actual input search and not just a call to the action
   *    to reset the search results
   */
  onSearch(query, isActualSearch) {
    this._onSearch(query, isActualSearch);
  },

  _onSearch(query, isActualSearch) {
    let results = List();
    let next = this.getState();

    // If user clears search field remove results

    if (query.length < 1) {
      next = next.set('results', results).set('searchQuery', query);
      return this.setStateAndTrigger(next);
    }

    if (isActualSearch) {
      next = next
        .set('noResultsSubtitle', 'Enter your search term above')
        .set('noResultsTitle', 'No Results!');

      this.setStateAndTrigger(next);
    }

    this.getInState('index').forEach((index) => {
      // index is all note cells/data cells/item titles
      let regex = new RegExp(escapeRegex(query), 'ig');
      let match = regex.exec(index.get('text'));

      if (!match || !index.get('text')) return;

      //Retrieve substring text that matches the query
      let subStr = index.get('text').substr(match.index);
      index = index.set('displayText', subStr);

      results = results.push(index);
    });

    next = next
      .set('activeResultIndex', null)
      .set('results', results)
      .set('searchQuery', query);

    this.setStateAndTrigger(next);

    this._checkForReplaceResultSelections();
  },
});
